import { z } from 'zod';

const THANKS_TITLE_MAX_LENGTH = 30;
const THANKS_MESSAGE_MAX_LENGTH = 1000;

/**
 * HTMLの構文を検証する関数
 * タグの開閉が正しく対応しているかチェック
 */
const validateHtml = (html: string): boolean => {
  if (!html.trim()) return true; // 空文字は許可

  try {
    // 自己閉じタグ（void elements）
    const voidElements = new Set([
      'area',
      'base',
      'br',
      'col',
      'embed',
      'hr',
      'img',
      'input',
      'link',
      'meta',
      'param',
      'source',
      'track',
      'wbr',
    ]);

    // タグを抽出する正規表現
    const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;
    const tags: Array<{ name: string; isClosing: boolean; isSelfClosing: boolean }> = [];
    let match;

    while ((match = tagRegex.exec(html)) !== null) {
      const fullTag = match[0];
      const tagName = match[1].toLowerCase();
      const isClosing = fullTag.startsWith('</');
      const isSelfClosing = fullTag.endsWith('/>') || voidElements.has(tagName);

      if (!isSelfClosing) {
        tags.push({ name: tagName, isClosing, isSelfClosing: false });
      }
    }

    // スタックを使ってタグの開閉をチェック
    const stack: string[] = [];
    for (const tag of tags) {
      if (tag.isClosing) {
        // 閉じタグの場合、対応する開きタグを探す
        if (stack.length === 0) {
          return false; // 閉じタグに対応する開きタグがない
        }
        const lastTag = stack.pop();
        if (lastTag !== tag.name) {
          return false; // タグの開閉が一致しない（例: <a></p>）
        }
      } else {
        // 開きタグの場合、スタックに追加
        stack.push(tag.name);
      }
    }

    // スタックが空でない場合、閉じられていないタグがある
    return stack.length === 0;
  } catch {
    return false;
  }
};

export const candidateFormThanksSchema = z.object({
  thanks_title: z
    .string()
    .max(THANKS_TITLE_MAX_LENGTH, `${THANKS_TITLE_MAX_LENGTH}文字以内で入力してください`)
    .optional()
    .nullable(),
  thanks_message: z
    .string()
    .max(
      THANKS_MESSAGE_MAX_LENGTH,
      `${THANKS_MESSAGE_MAX_LENGTH.toLocaleString()}文字以内で入力してください`
    )
    .optional()
    .nullable(),
  header_tag: z
    .string()
    .refine((val) => validateHtml(val || ''), {
      message: '有効なHTML形式で入力してください',
    })
    .optional()
    .nullable(),
});

export type CandidateFormThanksSchema = z.infer<typeof candidateFormThanksSchema>;
